import { Assert } from "../../Assert";
import { DataSource, IDataSource } from "../../Data/DataSource";
import { DataSourcePropertyValueChange } from "../../Data/DataSourcePropertyValueChange";
import { Constructor, Nullable, Nullish, PartialNullable, RadPromiseLike } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { EventHelper } from "../../Helpers/EventHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { LinqArray } from "../../LinqArray";
import { PropertyChange } from "../../PropertyChange";
import { IRadObject, Property, PropertyBoolean, PropertyInt32, PropertyObject, PropertyString, RadObject, RadObjectConstructor, RadObjectPropertyValues } from "../../RadObject";
import { ApplicationInformationService } from "../../RadWindow";
import { IEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { ServiceManager } from "../../ServiceManager";
import { DragDropManager } from "../DragDropManager";
import { ElementRef } from "../ElementRef";
import { JsxElement } from "../JsxElement";
import { JsxFragment } from "../JsxFragment";
import { IPage } from "../Pages/Page";
import { Point } from "../Point";
import { IContainerControl } from "./ContainerControl";
import { ContainerControlStatic } from "./ContainerControlStatic";
import { ControlRef } from "./ControlRef";
import { NAMESPACE } from "./_Namespace";

import "./Control.scss";

export class JsxRenderedElement
{
    $$element: JNative;
    events?: { $$target: JNative, eventName: string, fn: Function }[];

    constructor($$element: JNative)
    {
        this.$$element = $$element;
    }
}

interface RenderJsxContext
{
    events: { $$target: JNative, eventName: string, fn: Function }[];
}

function flattenChildArrays(children?: JsxFragment | string | (JsxFragment | string | undefined | (JsxFragment | string | undefined)[])[]): (JsxFragment | string)[]
{
    if (!children)
        return [];

    if (children instanceof JsxFragment)
        return [children];

    if (typeof children === "string")
        return [children];

    if (!children.length)
        return [];

    const
        newChildren: (JsxFragment | string)[] = [];

    for (let child of children)
    {
        if (child instanceof JsxFragment)
            newChildren.push(child);
        else if (typeof child === "string")
            newChildren.push(child);
        else if (child)
            newChildren.push(...child.filter(item => item != undefined) as (JsxFragment | string)[]);
    }

    return newChildren;
}

function flattenChildren(children?: JsxFragment | string | (JsxFragment | string | undefined | (JsxFragment | string | undefined)[])[]): (JsxFragment | string)[] | undefined
{
    if (!children)
        return;

    if (children instanceof JsxFragment)
        return [children];

    if (typeof children === "string")
        return [children];

    if (!children.length)
        return;

    const
        newChildren: (JsxFragment | string)[] = [];

    let
        currentChildren = flattenChildArrays(children),
        currentChildIndex = 0;

    const
        stack: { currentChildren: (JsxFragment | string)[], currentChildIndex: number }[] = [];

    while (currentChildren && currentChildIndex >= 0 && currentChildIndex < currentChildren.length)
    {
        const
            currentChild = currentChildren[currentChildIndex];

        if (currentChild instanceof JsxElement || typeof currentChild === "string")
        {
            newChildren.push(currentChild);
            currentChildIndex++;
        }
        else if (currentChild instanceof JsxFragment)
        {
            const
                children = flattenChildArrays(currentChild.children);

            if (children.length)
            {
                stack.push({ currentChildren, currentChildIndex });
                currentChildren = children;
                currentChildIndex = 0;
            }
        }
        else
            break;

        if (currentChildIndex === currentChildren.length)
        {
            const
                previous = stack.pop();

            if (!previous)
                break;

            currentChildren = previous.currentChildren;
            currentChildIndex = previous.currentChildIndex;
            currentChildIndex++;
        }
    }

    return newChildren;
}

function renderJsxInternal(jsxFragmentOrStringOrArray?: JsxFragment | string | (JsxFragment | string | undefined)[], element?: HTMLElement, _context?: RenderJsxContext): JsxRenderedElement[]
{
    if (!jsxFragmentOrStringOrArray)
        return [];

    let
        initialCall = false;

    if (!_context)
    {
        initialCall = true;
        _context = { events: [] };
    }

    const
        jsxFragments = flattenChildren(jsxFragmentOrStringOrArray)

    if (!jsxFragments)
        return [];

    const
        jsxRenderedElements: JsxRenderedElement[] = [];

    for (const jsxFragment of jsxFragments)
    {
        let
            jsxRenderedElement: JsxRenderedElement;

        if (typeof jsxFragment === "string")
            jsxRenderedElement = new JsxRenderedElement($$(JNativeStatic.createText(jsxFragment)));
        else
        {
            if (jsxFragment instanceof JsxElement)
            {
                if (jsxFragment.controlConstructor)
                {
                    const
                        control = new jsxFragment.controlConstructor({ ...jsxFragment.attributes, ...{ children: jsxFragment.children } });

                    if (jsxFragment.ref instanceof ControlRef)
                        jsxFragment.ref.current = control;

                    if (jsxFragment.data)
                        control.$$element.get()?.data(jsxFragment.data);

                    const
                        $$element = control.$$element.get();

                    if (!$$element)
                        continue;

                    jsxRenderedElement = new JsxRenderedElement($$element);
                }
                else
                {
                    jsxRenderedElement = new JsxRenderedElement(JNativeStatic.create$$({ nodeName: jsxFragment.nodeName, attributes: jsxFragment.attributes }));

                    if (jsxFragment.ref instanceof ElementRef)
                        jsxFragment.ref.current = jsxRenderedElement.$$element as JNative<HTMLElement>;

                    if (jsxFragment.data)
                        jsxRenderedElement.$$element.data(jsxFragment.data);

                    if (jsxFragment.children)
                    {
                        const
                            flattenedChildren = flattenChildren(jsxFragment.children);

                        if (flattenedChildren)
                        {
                            for (let i = 0; i < flattenedChildren.length; i++)
                            {
                                const
                                    item = flattenedChildren[i];

                                if (typeof item === "string")
                                    $$(JNativeStatic.createText(item)).appendTo(jsxRenderedElement.$$element);
                                else
                                    renderJsxInternal(item, undefined, _context).forEach(item => item.$$element.appendTo(jsxRenderedElement.$$element));
                            }
                        }
                    }
                }

                if (jsxFragment.events)
                {
                    for (let key in jsxFragment.events)
                        _context.events.push({ $$target: jsxRenderedElement.$$element, eventName: key, fn: jsxFragment.events[key] });
                }
            }
            else
            {
                jsxRenderedElement = new JsxRenderedElement($$());

                if (jsxFragment.children)
                {
                    const
                        flattenedChildren = flattenChildren(jsxFragment.children),
                        renderedChildren = renderJsxInternal(flattenedChildren, undefined, _context);

                    for (let i = 0; i < renderedChildren.length; i++)
                    {
                        const
                            item = renderedChildren[i];

                        if (item)
                            jsxRenderedElement.$$element.push.apply(jsxRenderedElement.$$element, item.$$element);
                    }
                }
            }
        }

        if (initialCall && _context.events.length)
            jsxRenderedElement.events = _context.events;

        if (element)
            jsxRenderedElement.$$element.appendTo(element);

        jsxRenderedElements.push(jsxRenderedElement);
    }

    return jsxRenderedElements;
}

export function renderJsx(jsxFragment: JsxFragment | string, element?: HTMLElement): JsxRenderedElement[] | undefined
{
    return renderJsxInternal(jsxFragment, element);
}

let
    waitInterval = 100,
    scrollEndTimeoutId = 0,
    scrollEndHandler = function (this: any)
    {
        const
            $$this = $$(this);

        if (scrollEndTimeoutId)
            clearTimeout(scrollEndTimeoutId);

        scrollEndTimeoutId = window.setTimeout(() => { $$this.trigger("scrollend"); }, waitInterval);
    },
    resizeEndTimeoutId = 0,
    resizeEndHandler = function (this: any)
    {
        const
            $$this = $$(this),
            $$element = this === window ? $$("html") : $$this;

        if (resizeEndTimeoutId)
            clearTimeout(resizeEndTimeoutId);

        if (!$$element.hasClass("rad-state-resizing"))
        {
            $$element.addClass("rad-state-resizing")
            $$this.trigger("resizestart");
        }

        resizeEndTimeoutId = window.setTimeout(() => { $$element.removeClass("rad-state-resizing"); $$this.trigger("resizeend"); }, waitInterval);
    },
    swipeThreshold = 20,
    swipeStartInputCoords: Point | undefined,
    swipeStartHandler = function (evt: IJNativeEvent)
    {
        swipeStartInputCoords = EventHelper.getInputCoordinates(evt);
    },
    swipeEndHandler = function (this: any, evt: IJNativeEvent)
    {
        if (!swipeStartInputCoords)
            return;

        const
            $$this = $$(this),
            swipeEndInputCoords = EventHelper.getInputCoordinates(evt);

        if (swipeEndInputCoords)
        {
            const
                xDistance = Math.abs(swipeEndInputCoords.x - swipeStartInputCoords.x),
                yDistance = Math.abs(swipeEndInputCoords.y - swipeStartInputCoords.y);

            if (xDistance > swipeThreshold || yDistance > swipeThreshold)
            {
                if (xDistance >= yDistance)
                {
                    // Horizontal
                    if (swipeEndInputCoords.x < swipeStartInputCoords.x)
                        $$this.triggerHandler("swipe", { direction: "left" });
                    else
                        $$this.triggerHandler("swipe", { direction: "right" });
                }
                else
                {
                    // Vertical
                    if (swipeEndInputCoords.y < swipeStartInputCoords.y)
                        $$this.triggerHandler("swipe", { direction: "up" });
                    else
                        $$this.triggerHandler("swipe", { direction: "down" });
                }
            }
        }

        swipeStartInputCoords = undefined;
    };

JNativeStatic.event.special.scrollend = {
    setup: function ()
    {
        $$(this).on("scroll", scrollEndHandler);
    },
    teardown: function ()
    {
        $$(this).off("scroll", scrollEndHandler);
    }
};
JNativeStatic.event.special.resizeend = {
    setup: function ()
    {
        $$(this).on("resize", resizeEndHandler);
    },
    teardown: function ()
    {
        $$(this).off("resize", resizeEndHandler);
    }
};
JNativeStatic.event.special.swipe = {
    setup: function ()
    {
        const
            $$this = $$(this);

        $$this.on("mousedown touchstart", swipeStartHandler);
        $$this.on("mouseup touchend", swipeEndHandler);
    },
    teardown: function ()
    {
        const
            $$this = $$(this);

        $$this.off("mousedown touchstart", swipeStartHandler);
        $$this.off("mouseup touchend", swipeEndHandler);
    }
};

// #region Control

export const
    FOCUSABLE_ELEMENTS_SELECTOR = "a, button, input, select, textarea";

export type ControlPropertyValues =
    RadObjectPropertyValues
    & {
        $$element: JNative<HTMLElement>;
        children: JsxFragment | string | (JsxFragment | string | undefined)[],
        controlClass: string;
        controlNamespace: string;
        cssClass: string;
        dataSource: IDataSource;
        dataSourceControlSelector: string;
        dataSourceControl: IControl;
        designMode: boolean;
        dimmed: boolean;
        draggable: boolean;
        elementTitle: string;
        elementTypeOverride: string;
        enabled: boolean;
        height: number;
        inline: boolean;
        isVisible: boolean;
        left: number;
        maxHeight: number;
        maxWidth: number;
        minHeight: number;
        minWidth: number;
        page: IPage;
        pageVariableName: string;
        propagateInputEvents: boolean;
        ref: ControlRef | ElementRef,
        suspendLayout: boolean;
        tabStop: boolean;
        tag: any;
        text: string;
        top: number;
        visible: boolean;
        visibleEvaluator: IEvaluator;
        width: number;
    };

export type ControlProperties = {
    [name in keyof ControlPropertyValues]: Property<ControlPropertyValues[name]>;
};

export interface IControl extends IRadObject, ControlProperties
{
    controlId?: string;
    labelTargetId?: string;

    position: Property<{ left?: Nullable<number>, top?: Nullable<number> }>;
    size: Property<{ height?: Nullable<number>, width?: Nullable<number> }>;

    _getFocusElement(): Nullable<JNative<HTMLElement>>;

    addCssClass(className: string): void;
    after(controlOrElementOrSelector: IControl | any[] | HTMLElement | string): this;
    appendTo($$elementOrContainerControl: Nullish<any[] | IContainerControl | HTMLElement | string>, beforeSelector?: any): this;
    before(controlOrElementOrSelector: IControl | any[] | HTMLElement | string): this;
    bringToFront(): void;
    clearCssClasses(): void;
    detach(): void;
    focus(): void;
    focusNextControl(): void;
    getEventHandlerTarget(eventName: string): any;
    getScrollParent(): Nullish<JNative<object | Document | Window | Element>>;
    hasCssClass(className: string): boolean;
    hide(): void;
    nextControl(): any;
    parentControlGet(): IControl | undefined;
    parentControlOfTypeGet(type: any): IControl | undefined;
    parentPageGet(): IControl | undefined;
    prependTo($$elementOrContainerControl: Nullish<any[] | IContainerControl | HTMLElement | string>): this;
    previousControl(): IControl | undefined;
    refreshFromDataSource(): void;
    remove(): void;
    removeControls(controls: (Nullish<{ remove: Function }>)[]): void;
    removeCssClass(className: string): void;
    scrollToAsync(): Promise<any>;
    sendToBack(): void;
    show(): void;
    viewElementGet(): JNative<HTMLElement>;
    updateVisibility(): void;
}

export type ControlConstructor = RadObjectConstructor & {
}

export abstract class Control<TPropertyValues extends ControlPropertyValues = ControlPropertyValues> extends RadObject<TPropertyValues> implements IControl
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Control";
    public static override className = "Control";

    // #region Properties

    public static readonly $$detachedElements = JNativeStatic.create$$({ nodeName: "div" });
    public static readonly controlIdFactory =
        {
            id: 0,
            getNextId: function ()
            {
                return this.id++;
            }
        };
    public static debugInformation?: {
        controls: IControl[],
        previouslySummarizedControls: IControl[],
        summarize: any,
    };
    public static readonly htmlEventNames = {
        abort: true,
        afterprint: true,
        animationend: true,
        animationiteration: true,
        animationstart: true,
        beforeprint: true,
        beforeunload: true,
        blur: true,
        canplay: true,
        canplaythrough: true,
        change: true,
        click: true,
        contextmenu: true,
        copy: true,
        cut: true,
        dblclick: true,
        drag: true,
        dragend: true,
        dragenter: true,
        dragleave: true,
        dragover: true,
        dragstart: true,
        drop: true,
        durationchange: true,
        ended: true,
        error: true,
        focus: true,
        focusin: true,
        focusout: true,
        fullscreenchange: true,
        fullscreenerror: true,
        hashchange: true,
        input: true,
        invalid: true,
        keydown: true,
        keypress: true,
        keyup: true,
        load: true,
        loadeddata: true,
        loadedmetadata: true,
        loadstart: true,
        message: true,
        mousedown: true,
        mouseenter: true,
        mouseleave: true,
        mousemove: true,
        mouseover: true,
        mouseout: true,
        mouseup: true,
        mousewheel: true,
        offline: true,
        online: true,
        open: true,
        pagehide: true,
        pageshow: true,
        paste: true,
        pause: true,
        play: true,
        playing: true,
        popstate: true,
        progress: true,
        ratechange: true,
        resize: true,
        reset: true,
        scroll: true,
        search: true,
        seeked: true,
        seeking: true,
        select: true,
        show: true,
        stalled: true,
        storage: true,
        submit: true,
        suspend: true,
        timeupdate: true,
        toggle: true,
        touchcancel: true,
        touchend: true,
        touchmove: true,
        touchstart: true,
        transitionend: true,
        unload: true,
        volumechange: true,
        waiting: true,
        wheel: true
    };
    private static readonly controlClasses: { [key: string]: Constructor<IControl> } = {};

    // #endregion

    // #region Public Methods

    public static find(selector: string)
    {
        return (
            $$("body")
                .add(this.$$detachedElements)
                .find(selector)
                .radControl()
        );
    }
    public static registerControl(control: IControl)
    {
        if (!ServiceManager.getOptional(ApplicationInformationService)?.isDevelopment)
            return;

        if (!this.debugInformation)
        {
            this.debugInformation = {
                controls: [],
                previouslySummarizedControls: [],
                summarize: () =>
                {
                    Assert.isNotNullOrUndefined(this.debugInformation, "this.debugInformation");
                    ArrayHelper.removeWhere(this.debugInformation.controls, item => (item as any)._disposed === true);

                    const
                        controlArray = LinqArray.create(this.debugInformation.controls),
                        previouslySummarizedControlArray = LinqArray.create(this.debugInformation.previouslySummarizedControls);

                    const
                        delta =
                            controlArray
                                .where(item => !previouslySummarizedControlArray.any(item2 => item2 === item));

                    console.log({
                        delta: delta
                            .groupBy(
                                (item) => ({
                                    classFullName: (item.constructor as RadObjectConstructor).classFullName ?? item.constructor.name,
                                }),
                                (item) => item,
                                (key, elements) => ({
                                    key,
                                    elements,
                                })
                            )
                            .reduce((accumulator: any, currentValue) => (accumulator[currentValue.key.classFullName] = currentValue.elements, accumulator), {}),
                        summary: controlArray
                            .groupBy(
                                (item) => ({
                                    classFullName: (item.constructor as RadObjectConstructor).classFullName ?? item.constructor.name,
                                }),
                                (item) => item,
                                (key, elements) => ({
                                    key,
                                    elements,
                                })
                            )
                            .reduce((accumulator: any, currentValue) => (accumulator[currentValue.key.classFullName] = currentValue.elements, accumulator), {}),
                        total: controlArray.length,
                    });

                    this.debugInformation.previouslySummarizedControls =
                        this.debugInformation.controls.slice();
                }
            };
        }

        ArrayHelper.removeWhere(this.debugInformation.controls, item => (item as any)._disposed === true);
        this.debugInformation?.controls.push(control);
    }

    // #endregion

    // #region Overrides

    // #region Methods

    public static override createPropertyDefaults(): PartialNullable<ControlPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                cssClass: StringHelper.empty,
                enabled: true,
                inline: false,
                isVisible: true,
                propagateInputEvents: true,
                suspendLayout: false,
                tabStop: false,
                text: StringHelper.empty,
                visible: true
            }
        };
    }

    // #endregion

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public __refreshingFromDataSource: boolean;

    protected __refreshFromDataSourceSuspended: boolean;
    protected _controlOverlayLoader: ControlOverlayLoader | undefined;
    protected _controlOverlayLoaderQueueLength?: number;
    protected _showLoaderTimeoutId: number = 0;

    // #endregion

    // #region Properties

    public $$element!: Property<JNative<HTMLElement>>;
    public children!: Property<JsxFragment | string | (JsxFragment | string | undefined)[]>;
    public controlClass!: PropertyString;
    public controlNamespace!: PropertyString;
    public cssClass!: PropertyString;
    public dataSource!: Property<IDataSource>;
    public dataSourceControl!: Property<IControl>;
    public dataSourceControlSelector!: PropertyString;
    public designMode!: PropertyBoolean;
    public dimmed!: PropertyBoolean;
    public draggable!: PropertyBoolean;
    public elementTypeOverride!: PropertyString;
    public elementTitle!: PropertyString;
    public enabled!: PropertyBoolean;
    public height!: PropertyInt32;
    public inline!: PropertyBoolean;
    public isVisible!: PropertyBoolean;
    public left!: PropertyInt32;
    public maxHeight!: PropertyInt32;
    public maxWidth!: PropertyInt32;
    public minHeight!: PropertyInt32;
    public minWidth!: PropertyInt32;
    public page!: Property<IPage>;
    public pageVariableName!: PropertyString;
    public position!: Property<{ left?: Nullable<number>, top?: Nullable<number> }>;
    public propagateInputEvents!: PropertyBoolean;
    public ref!: Property<ControlRef | ElementRef>;
    public size!: Property<{ height?: Nullable<number>, width?: Nullable<number> }>;
    public suspendLayout!: PropertyBoolean;
    public tabStop!: PropertyBoolean;
    public tag!: PropertyObject;
    public text!: PropertyString;
    public top!: PropertyInt32;
    public visible!: PropertyBoolean;
    public visibleEvaluator!: Property<IEvaluator>;
    public width!: PropertyInt32;

    public controlId?: string;
    public labelTargetId?: string;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        if (ServiceManager.getOptional(ApplicationInformationService)?.isDevelopment)
            Control.registerControl(this);

        this.__refreshingFromDataSource = false;
        this.__refreshFromDataSourceSuspended = false;

        this._updatePropertiesFromElement();
        this._setIds();
        this.createElement();
    }

    // #endregion

    // #region Protected Methods

    protected _$$elementChanged(change: PropertyChange<JNative>)
    {
        if (change.originalValue)
            change.originalValue.removeRadControl();

        if (change.currentValue)
            change.currentValue.radControl(this);

        this._setPage();
    }
    protected _appendToOrPrependTo(elementOrContainerControl: Nullish<any[] | IContainerControl | HTMLElement | string>, append: boolean, beforeSelector?: any)
    {
        if (!elementOrContainerControl)
            return;

        const
            element = this.$$element.get()?.get(0);

        if (!element)
            return;

        let
            target: any = elementOrContainerControl;

        if (typeof elementOrContainerControl === "string" || ArrayHelper.isArrayLike(elementOrContainerControl))
            target = $$(elementOrContainerControl).get(0);

        if (!target)
            return;

        if (target instanceof HTMLElement)
        {
            const
                originalParent = element.parentElement;

            let
                beforeElements;

            if (beforeSelector)
                beforeElements = target.querySelectorAll(beforeSelector);

            if (beforeElements && beforeElements.length)
                target.insertBefore(element, beforeElements[0]);
            else if (append)
                target.append(element);
            else
                target.prepend(element);

            [element]
                .concat(Array.from(element.querySelectorAll(".rad-control")))
                .forEach(item => { JNativeStatic.radControl(item)?.triggerHandler("ancestorChanged") });

            this.triggerHandler("parentChanged", { originalValue: originalParent ? $$(originalParent) : null, currentValue: $$(target) });

            return;
        }

        if (ObjectHelper.isAssignableTo<IContainerControl>(target, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ContainerControl.ContainerControl"))
        {
            if (append)
                target.append(this, beforeSelector);
            else
                target.prepend(this, beforeSelector);

            return;
        }

        throw new Error("Only HTMLElements and ContainerControls are supported.");
    }
    protected _bindEvents()
    {
        this.on("ancestorChanged", { self: this }, this.onAncestorChanged);
        this.on("ancestorVisibilityChanged", { self: this }, this.onAncestorVisibilityChanged);
        this.on("click", { self: this }, this.onClick);
        this.on("parentChanged", { self: this }, this.onParentChanged);
        this.on("mouseup", { self: this }, this.onMouseUp);

        this.on("keydown", { self: this }, this.onKeyDown);
        this.on("keypress", { self: this }, this.onKeyPress);
        this.on("keyup", { self: this }, this.onKeyUp);
    }
    protected _controlClassChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _controlNamespaceChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _createElement(): JNative<HTMLElement>
    {
        const
            jsxElement = this._createElementHtml(this.children?.get() ?? undefined),
            jsxRenderedElements: JsxRenderedElement[] | undefined = renderJsx(jsxElement);

        let
            $$element: JNative<HTMLElement>;

        if (jsxRenderedElements && jsxRenderedElements.length)
        {
            $$element = jsxRenderedElements[0].$$element as JNative<HTMLElement>;

            for (let jsxRenderedElement of jsxRenderedElements)
            {
                if (jsxRenderedElement && jsxRenderedElement.events?.length)
                {
                    for (let evt of jsxRenderedElement.events)
                        evt.$$target.on(evt.eventName, { self: this }, evt.fn, this);
                }
            }
        }
        else
            $$element = JNativeStatic.create$$("div");

        $$element.attr("id", this.controlId ?? null);
        $$element.radControl(this);

        const
            cssClass = this.cssClass.get();

        if (cssClass)
            $$element.addClass(cssClass);

        const
            element = $$element.get(0);

        if (element)
        {
            // TODO: BAM
            [element]
                .concat(Array.from(element.querySelectorAll(".rad-control")))
                .forEach(item => { JNativeStatic.radControl(item)?.triggerHandler("ancestorChanged") });
        }

        return $$element;
    }
    protected _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[]): JsxFragment
    {
        return (
            <div>
                {children}
            </div>
        );
    }
    protected _createElementComplete()
    {
        if (ObjectHelper.isAssignableTo<IPage>(this, "@seriously-rad/seriouslyrad-framework-core/Ui/Pages/Page.Page"))
            this.page.set(this);

        // TODO: BAM
        //this._savePropertyDefaults();

        this._bindEvents();

        this.addCssClass(this.cssClass.get() ?? "");

        // Trigger Property Changes
        this.$$element.triggerChange();
        this.dataSource.triggerChange();
        this.dataSourceControl.triggerChange();
        this.designMode.triggerChange();
        this.dimmed.triggerChange();
        this.draggable.triggerChange();
        this.elementTypeOverride.triggerChange();
        this.elementTitle.triggerChange();
        this.enabled.triggerChange();
        this.height.triggerChange();
        this.inline.triggerChange();
        this.isVisible.triggerChange();
        this.left.triggerChange();
        this.maxHeight.triggerChange();
        this.maxWidth.triggerChange();
        this.minHeight.triggerChange();
        this.minWidth.triggerChange();
        this.page.triggerChange();
        this.tabStop.triggerChange();
        this.text.triggerChange();
        this.top.triggerChange();
        this.visible.triggerChange();
        this.visibleEvaluator.triggerChange();
        this.width.triggerChange();

        this.updateVisibility();

        this.dataSourceControl.set(this._getDataSourceControl());
        this.dataSourceControl.triggerChange();
    }
    protected _cssClassChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _dataSourceChanged(change: PropertyChange<IDataSource>)
    {
        if (change.originalValue)
        {
            change.originalValue.off("afterSaveChanges", { self: this }, this.onDataSourceAfterSaveChanges);
            change.originalValue.off("beforeSaveChanges", { self: this }, this.onDataSourceBeforeSaveChanges);
            change.originalValue.off("propertyValueChanged", { self: this }, this.onDataSourcePropertyValueChanged);
            change.originalValue.off("validating", { self: this }, this.onDataSourceValidating);
            change.originalValue.off("validationErrorsChanged", { self: this }, this.onDataSourceValidationErrorsChanged);
        }

        if (change.currentValue)
        {
            change.currentValue.on("afterSaveChanges", { self: this }, this.onDataSourceAfterSaveChanges);
            change.currentValue.on("beforeSaveChanges", { self: this }, this.onDataSourceBeforeSaveChanges);
            change.currentValue.on("propertyValueChanged", { self: this }, this.onDataSourcePropertyValueChanged);
            change.currentValue.on("validating", { self: this }, this.onDataSourceValidating);
            change.currentValue.on("validationErrorsChanged", { self: this }, this.onDataSourceValidationErrorsChanged);
        }

        this.refreshFromDataSource();

        this.triggerHandler("dataSourceChanged", change);
    }
    protected _dataSourceControlChanged(change: PropertyChange<IControl>)
    {
        if (change.originalValue)
            change.originalValue.off("dataSourceChanged", { self: this }, this.onDataSourceControlDataSourceChanged);

        if (change.currentValue)
            change.currentValue.on("dataSourceChanged", { self: this }, this.onDataSourceControlDataSourceChanged);
    }
    protected _dataSourceControlSelectorChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _designModeChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-design");
        else
            this.removeCssClass("rad-state-design");
    }
    protected _dimmedChanged(change: PropertyChange<boolean>)
    {
        this._updateTabIndexAttribute();
    }
    protected _dispose()
    {
        const
            dataSourceControl = this.dataSourceControl.get();

        if (dataSourceControl)
            dataSourceControl.off("dataSourceChanged", { self: this }, this.onDataSourceControlDataSourceChanged);

        const
            dataSource = this.dataSource.get();

        if (dataSource)
        {
            dataSource.off("afterSaveChanges", { self: this }, this.onDataSourceAfterSaveChanges);
            dataSource.off("beforeSaveChanges", { self: this }, this.onDataSourceBeforeSaveChanges);
            dataSource.off("propertyValueChanged", { self: this }, this.onDataSourcePropertyValueChanged);
            dataSource.off("validating", { self: this }, this.onDataSourceValidating);
            dataSource.off("validationErrorsChanged", { self: this }, this.onDataSourceValidationErrorsChanged);
        }

        const
            visibleEvaluator = this.visibleEvaluator.get();

        if (visibleEvaluator)
            visibleEvaluator.off("valueChanged", { self: this }, this.onVisibleEvaluatorValueChanged);

        this.$$element.get()?.dataRemove();
        this.$$element.set(null);

        super._dispose();
    }
    protected _draggableChanged(change: PropertyChange<boolean>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        if (change.currentValue)
        {
            DragDropManager.instance.createDragDropItems($$element);
            DragDropManager.instance.createDragDropHandles($$element);
        }
        else
        {
            DragDropManager.instance.disableDragDropItems($$element);
            DragDropManager.instance.disableDragDropHandles($$element);
        }
    }
    protected _elementTitleChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
        {
            this.$$element.get()?.attr("aria-label", change.currentValue);
            this.$$element.get()?.attr("title", change.currentValue);
        }
        else
        {
            this.$$element.get()?.removeAttr("aria-label");
            this.$$element.get()?.removeAttr("title");
        }
    }
    protected _elementTypeOverrideChanged(change: PropertyChange<string>)
    {
        if (change.currentValue)
            this.$$element.get()?.changeTagName(change.currentValue);
    }
    protected _enabledChanged(change: PropertyChange<boolean>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        if (change.currentValue)
        {
            $$element.removeClass("disabled");
            $$element.removeAttr("disabled");
        }
        else
        {
            $$element.addClass("disabled");
            $$element.attr("disabled", "disabled");
        }

        this._updateTabIndexAttribute();
    }
    protected _getDataSourceControl()
    {
        const
            $$element = this.$$element.get(),
            dataSourceControlSelector = this.dataSourceControlSelector.get();

        if ($$element && dataSourceControlSelector)
            return $$element.closest(dataSourceControlSelector).radControl();

        return this.dataSourceControl.get();
    }
    // TODO Should be protected
    public _getFocusElement(): Nullable<JNative<HTMLElement>>
    {
        return this.$$element.get();
    }
    protected _getScrollParent($$element: JNative<HTMLElement>): JNative | undefined
    {
        if (!$$element.length)
            return undefined;

        if ($$element[0].scrollHeight > $$element[0].clientHeight)
            return $$element;
        else
            return this._getScrollParent($$element.parent());
    }
    protected _heightChanged(change: PropertyChange<number>)
    {
        const
            element = this.$$element.get()?.get(0);

        if (!element)
            return;

        const
            original = element.style.height !== undefined && !StringHelper.isNullOrWhitespace(element.style.height) ? element.style.height : "",
            current = change.currentValue !== null ? StringHelper.format("{0}px", change.currentValue) : "";

        if (current !== original)
            element.style.height = current;
    }
    protected _inlineChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-inline");
        else
            this.removeCssClass("rad-inline");
    }
    protected _isShown()
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        return ($$element.outerHeight() ?? 0) > 0 || ($$element.outerWidth() ?? 0) > 0;
    }
    protected _isVisibleChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.removeCssClass("rad-state-hidden");
        else
            this.addCssClass("rad-state-hidden");

        this.trigger("isVisibleChanged", change);
        this._triggerAncestorVisibilityChanged(change);
    }
    protected _leftChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        $$element.css({ left: change.currentValue });
    }
    public _loadingDequeue(hideImmediately?: boolean)
    {
        if (!this._controlOverlayLoader)
            return;

        if (ObjectHelper.isNullOrUndefined(this._controlOverlayLoaderQueueLength))
            this._controlOverlayLoaderQueueLength = 0;

        Assert.isNotNullOrUndefined(this._controlOverlayLoaderQueueLength, "this._controlOverlayLoaderQueueLength");

        this._controlOverlayLoaderQueueLength--;

        if (this._controlOverlayLoaderQueueLength < 0)
            this._controlOverlayLoaderQueueLength = 0;

        if (this._controlOverlayLoaderQueueLength === 0)
        {
            if (this._showLoaderTimeoutId)
            {
                clearTimeout(this._showLoaderTimeoutId);
                this._showLoaderTimeoutId = 0;
            }

            hideImmediately
                ? this._controlOverlayLoader.hideImmediately()
                : this._controlOverlayLoader.hide();
        }
    }
    public _loadingEnqueue()
    {
        if (!this._controlOverlayLoader)
        {
            this._controlOverlayLoader = new ControlOverlayLoader();

            const
                $$controlOverlayLoader = this._controlOverlayLoader.$$element.get();

            if ($$controlOverlayLoader)
                this.$$element.get()?.append($$controlOverlayLoader);
        }

        if (ObjectHelper.isNullOrUndefined(this._controlOverlayLoaderQueueLength))
            this._controlOverlayLoaderQueueLength = 0;

        Assert.isNotNullOrUndefined(this._controlOverlayLoaderQueueLength, "this._controlOverlayLoaderQueueLength");

        this._controlOverlayLoaderQueueLength++;

        if (!this._showLoaderTimeoutId)
        {
            this._showLoaderTimeoutId = window.setTimeout(() =>
            {
                this._showLoaderTimeoutId = 0;
                this._controlOverlayLoader!.show();
            }, 250);
        }
    }
    protected _maxHeightChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        $$element.css({ maxHeight: change.currentValue });
    }
    protected _maxWidthChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        $$element.css({ maxWidth: change.currentValue });
    }
    protected _minHeightChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        $$element.css({ minHeight: change.currentValue });
    }
    protected _minWidthChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        $$element.css({ minWidth: change.currentValue });
    }
    protected _pageChanged(change: PropertyChange<IPage>)
    {
        const
            pageVariableName = this.pageVariableName.get();

        if (change.originalValue)
        {
            if (pageVariableName)
                delete (change.originalValue as any)[pageVariableName];

            change.originalValue.off("dataSourceChanged", { self: this }, this.onPageDataSourceChanged);
        }

        if (change.currentValue)
        {
            if (pageVariableName)
                (change.originalValue as any)[pageVariableName] = this;

            change.currentValue.on("dataSourceChanged", { self: this }, this.onPageDataSourceChanged);
        }
    }
    protected _pageVariableNameChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _positionGet()
    {
        return {
            left: this.left.get(),
            top: this.top.get()
        };
    }
    protected _positionSet(value: { left?: Nullable<number>, top?: Nullable<number> })
    {
        if (value)
        {
            this.left.set(value.left !== undefined ? value.left : null);
            this.top.set(value.top !== undefined ? value.top : null);
        }
    }
    protected _propagateInputEventsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _refreshFromDataSource()
    {
        /* Intentionally empty */
    }
    public _resumeRefreshFromDataSource()
    {
        this.__refreshFromDataSourceSuspended = false;
        this.refreshFromDataSource();
    }
    //protected _savePropertyDefaults()
    //{
    //    const
    //        propertyDefaults: any = {};

    //    Object.keys(this)

    //    this[".classDefinition"].properties
    //        .each(item =>
    //        {
    //            switch (item.type)
    //            {
    //                case "array":
    //                case "boolean":
    //                case "float":
    //                case "int":
    //                case "string":
    //                    propertyDefaults[item.name] = this[".properties"][item.name] !== undefined ? this[".properties"][item.name] : null;
    //                    break;
    //            }
    //        });

    //    this[".propertyDefaults"] = propertyDefaults;
    //}
    protected _setPage()
    {
        let
            page: IControl | undefined = this;

        while (page)
        {
            if (ObjectHelper.isAssignableTo<IPage>(page, "@seriously-rad/seriouslyrad-framework-core/Ui/Pages/Page.Page"))
                break;

            page = page.parentPageGet();
        }

        this.page.set(page ? page : null);
    }
    protected _setIds()
    {
        const
            $$element = this.$$element.get(),
            element = $$element?.length ? $$element[0] : null;

        this.controlId = element?.getAttribute("id") ?? "control-" + Control.controlIdFactory.getNextId();
        this.labelTargetId = this.controlId;
    }
    protected _sizeGet()
    {
        return {
            width: this.width.get(),
            height: this.height.get()
        };
    }
    protected _sizeSet(value: { height?: Nullable<number>, width?: Nullable<number> })
    {
        if (value)
        {
            this.width.set(value.width !== undefined ? value.width : null);
            this.height.set(value.height !== undefined ? value.height : null);
        }
    }
    protected _suspendLayoutChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    public _suspendRefreshFromDataSource()
    {
        this.__refreshFromDataSourceSuspended = true;
    }
    protected _tabStopChanged(change: PropertyChange<boolean>)
    {
        this._updateTabIndexAttribute();
    }
    protected _tagChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _textChanged(change: PropertyChange<string>)
    {
        this.triggerHandler("textChanged", change);
    }
    protected _topChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        $$element.css({ top: change.currentValue });
    }
    public _triggerAncestorVisibilityChanged(change: PropertyChange<boolean>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        const
            $$controlElements = $$element.find(".rad-control");

        for (let controlElement of $$controlElements)
        {
            if (!(controlElement instanceof HTMLElement))
                continue;

            JNativeStatic.radControl(controlElement)?.triggerHandler("ancestorVisibilityChanged", change);
        }
    }
    protected _updatePropertiesFromElement()
    {
        const
            $$element = this.$$element?.get();

        if (!$$element)
            return;

        const
            data = $$element.dataGet();

        for (let dataName in data)
        {
            const
                property = (this as any)[dataName];

            if (property instanceof Property)
                property.set(data[dataName], false);
        }


        //    propertyDefinitions = this[".classDefinition"].properties;

        //for (let i = 0; i < propertyDefinitions.length; i++)
        //{
        //    const
        //        propertyDefinition = propertyDefinitions[i],
        //        dataName = propertyDefinition.name;

        //    if (Object.prototype.hasOwnProperty.call(data, dataName))
        //        this[".properties"][propertyDefinition.name] = ObjectHelper.asType(data[dataName], propertyDefinition.type);
        //}
    }
    protected _updateTabIndexAttribute()
    {
        const
            $$focusElement = this._getFocusElement();

        if (!$$focusElement)
            return;

        const
            hasTabIndex = this.tabStop.get() && this.enabled.get();

        if (hasTabIndex)
        {
            if (this.dimmed.get())
            {
                $$focusElement.attr("tabindex", "-1");
                $$focusElement.attr("data-tabindex-control", "0");
                $$focusElement.attr("data-tabindex-backup", "0");
            }
            else
            {
                $$focusElement.attr("tabindex", "0");
                $$focusElement.attr("data-tabindex-control", "0");
                $$focusElement.removeAttr("data-tabindex-backup");
            }
        }
        else
        {
            $$focusElement.removeAttr("tabindex");
            $$focusElement.removeAttr("data-tabindex-control");
            $$focusElement.removeAttr("data-tabindex-backup");
        }
    }
    protected _visibleChanged(change: PropertyChange<boolean>)
    {
        this.updateVisibility();
    }
    protected _visibleEvaluatorChanged(change: PropertyChange<IEvaluator>)
    {
        if (change.originalValue)
        {
            change.originalValue.off("valueChanged", { self: this }, this.onVisibleEvaluatorValueChanged);
            change.originalValue.context.set(null);
        }

        if (change.currentValue)
        {
            change.currentValue.on("valueChanged", { self: this }, this.onVisibleEvaluatorValueChanged);
            change.currentValue.context.set(this);
        }

        this.updateVisibility();
    }
    protected _widthChanged(change: PropertyChange<number>)
    {
        const
            element = this.$$element.get()?.get(0);

        if (!element)
            return;

        const
            original = element.style.width !== undefined && !StringHelper.isNullOrWhitespace(element.style.width) ? element.style.width : "",
            current = change.currentValue !== null ? StringHelper.format("{0}px", change.currentValue) : "";

        if (current !== original)
            element.style.width = current;
    }

    // #endregion

    // #region Public Methods

    /**
     * 
     * Adds a CSS class to the control element.
     *
     * @param className The CSS class name
     *
     */
    public addCssClass(className: string): void
    {
        if (this._disposed)
            return;

        this.$$element.get()?.addClass(className);
    }
    public after(controlOrElementOrSelector: IControl | any[] | HTMLElement | string): this
    {
        let
            $$elementAfter: Nullish<JNative>;

        if (controlOrElementOrSelector instanceof Control)
            $$elementAfter = controlOrElementOrSelector.$$element.get();
        else if (typeof controlOrElementOrSelector === "string" || ArrayHelper.isArrayLike(controlOrElementOrSelector) || controlOrElementOrSelector instanceof HTMLElement)
            $$elementAfter = $$(controlOrElementOrSelector);

        if ($$elementAfter?.length)
            $$elementAfter.after(this.$$element.get());

        return this;
    }
    public appendTo($$elementOrContainerControl: Nullish<any[] | IContainerControl | HTMLElement | string>, beforeSelector?: any): this
    {
        this._appendToOrPrependTo($$elementOrContainerControl, true, beforeSelector);
        return this;
    }
    public before(controlOrElementOrSelector: IControl | any[] | HTMLElement | string): this
    {
        let
            $$elementBefore: Nullish<JNative>;

        if (controlOrElementOrSelector instanceof Control)
            $$elementBefore = controlOrElementOrSelector.$$element.get();
        else if (typeof controlOrElementOrSelector === "string" || ArrayHelper.isArrayLike(controlOrElementOrSelector) || controlOrElementOrSelector instanceof Element)
            $$elementBefore = $$(controlOrElementOrSelector);

        if ($$elementBefore?.length)
            $$elementBefore.before(this.$$element.get());

        return this;
    }
    public bringToFront(): void
    {
        const
            parentControl = this.parentControlGet();

        if (parentControl && ObjectHelper.isAssignableTo<IContainerControl>(parentControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ContainerControl.ContainerControl"))
        {
            parentControl.append(this);
            return;
        }

        const
            $$element = this.$$element.get();

        if ($$element)
            $$element.parent()?.append($$element);
    }
    public clearCssClasses(): void
    {
        if (this._disposed)
            return;

        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        $$element.removeClass();
    }
    public createElement(): JNative<HTMLElement>
    {
        let
            $$element = this.$$element.get();

        if (!$$element)
        {
            $$element = this._createElement();
            this.$$element.set($$element);
            this._createElementComplete();
        }

        return $$element;
    }
    //public dataSourceSet(value: any)
    //{
    //    if (value && !(value instanceof DataSource))
    //        value = new DataSource({ data: value });

    //    return this._dataSourceSet(value);
    //}
    //public designablePropertiesGet()
    //{
    //    return this[".classDefinition"].designablePropertiesGet();
    //}
    //public designablePropertyValuesGet(changedOnly: boolean)
    //{
    //    const
    //        designablePropertyValues = {};
    //
    //    let
    //        value;

    //    this.designableProperties.get()
    //        .each(item =>
    //        {
    //            value = this[item.name + "Get"]();

    //            if (changedOnly && value === this[".propertyDefaults"][item.name])
    //                return;

    //            designablePropertyValues[item.name] = value;
    //        });

    //    return designablePropertyValues;
    //}
    public detach(): void
    {
        const
            $$element = this.$$element.get();

        if ($$element)
            Control.$$detachedElements.append($$element);
    }
    public focus(): void
    {
        const
            $$focusElement = this._getFocusElement();

        if (!$$focusElement)
            return;

        $$focusElement.focus();
    }
    public focusNextControl(): void
    {
        const
            $$focusElement = this._getFocusElement();

        if (!$$focusElement || !$$focusElement.length)
            return;

        const
            $$inputs = $$(FOCUSABLE_ELEMENTS_SELECTOR + ", [tabindex]");

        if (!$$inputs.length)
            return;

        let
            nextIndex = $$inputs.index($$focusElement[0]) + 1;

        if (nextIndex >= $$inputs.length)
            nextIndex = 0;

        const
            nextInputElement = $$inputs.get(nextIndex);

        if (!nextInputElement)
            return;

        const
            control = $$(nextInputElement).closest(".rad-control").radControl();

        if (control)
            control.focus();
    }
    public getEventHandlerTarget(eventName: string): any
    {
        if ((Control.htmlEventNames as any)[eventName])
            return this.$$element.get();

        if (JNativeStatic.event.special[eventName])
            return this.$$element.get();

        return super.getEventHandlerTarget();
    }
    public getScrollParent(): Nullish<JNative<object | Document | Window | Element>>
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return null;

        return this._getScrollParent($$element.parent());
    }
    public hasCssClass(className: string): boolean
    {
        return this.$$element.get()?.hasClass(className) ?? false;
    }
    public hide(): void
    {
        this.visible.set(false);
    }
    public nextControl(): any
    {
        let
            $$element = this.$$element.get(),
            finished = false;

        while (!finished && $$element)
        {
            $$element = $$element.next();

            if (!$$element.length)
            {
                finished = true;
                continue;
            }

            const
                control = $$element.radControl();

            if (control)
                return control;
        }

        return null;
    }
    public parentControlGet(): IControl | undefined
    {
        return this.parentControlOfTypeGet("@seriously-rad/seriouslyrad-framework-core/Ui/Controls/Control.Control");
    }
    public parentControlOfTypeGet(classFullName: string): IControl | undefined
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return undefined;

        let
            currentElement = $$element.get(0),
            currentControl;

        while (currentElement && currentElement.parentElement)
        {
            currentElement = currentElement.parentElement.closest(".rad-control") as HTMLElement;

            if (!currentElement)
                return undefined;

            currentControl = JNativeStatic.radControl(currentElement);

            if (ObjectHelper.isAssignableTo<IControl>(currentControl, classFullName))
                return currentControl;
        }

        return undefined;
    }
    public parentPageGet(): IControl | undefined
    {
        return this.parentControlOfTypeGet("@seriously-rad/seriouslyrad-framework-core/Ui/Pages/Page.Page");
    }
    public prependTo($$elementOrContainerControl: Nullish<any[] | IContainerControl | HTMLElement | string>): this
    {
        this._appendToOrPrependTo($$elementOrContainerControl, false);
        return this;
    }
    public previousControl(): IControl | undefined
    {
        let
            $$element = this.$$element.get(),
            finished = false;

        while (!finished && $$element)
        {
            $$element = $$element.prev();

            if (!$$element.length)
            {
                finished = true;
                continue;
            }

            const
                control = $$element.radControl();

            if (control)
                return control as IControl;
        }
    }
    public refreshFromDataSource(): void
    {
        if (this.__refreshFromDataSourceSuspended)
            return;

        if (this.__refreshingFromDataSource)
            return;

        this.__refreshingFromDataSource = true;
        this._refreshFromDataSource();
        this.__refreshingFromDataSource = false;
    }
    public remove(): void
    {
        if (this._disposed)
            return;

        const
            $$element = this.$$element.get(),
            parentControl = this.parentControlGet();

        const
            items = [];

        if (this._controlOverlayLoader)
            items.push(this._controlOverlayLoader);

        if ($$element)
            items.push($$element);

        this.removeControls(items);

        if (ObjectHelper.isAssignableTo<IContainerControl>(parentControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ContainerControl.ContainerControl"))
            ContainerControlStatic._triggerControlsChanged(parentControl);

        window.setTimeout(() =>
        {
            this.dispose();
        });
    }
    public removeControls(controls: (Nullish<{ remove: Function }>)[]): void
    {
        controls.forEach(item => { item && item.remove(); });
    }
    public removeCssClass(className: string): void
    {
        if (this._disposed)
            return;

        this.$$element.get()?.removeClass(className);
    }
    public scrollToAsync(): Promise<any>
    {
        const
            $$element = this.$$element.get(),
            elementOffset = $$element ? $$element.offset() : undefined,
            $$scrollParent = this.getScrollParent(),
            scrollParentOffset = $$scrollParent ? $$scrollParent.offset() : undefined,
            scrollParentScrollTop = $$scrollParent ? $$scrollParent.scrollTop() : undefined;

        if (!$$element || !elementOffset || !$$scrollParent || !$$scrollParent.length || !scrollParentOffset || scrollParentScrollTop === undefined)
            return Promise.resolve();

        return (
            $$scrollParent
                .stop()
                .animate({
                    scrollTop: elementOffset.top - scrollParentOffset.top + scrollParentScrollTop
                }, 500)
                .promise()
        );
    }
    public sendToBack(): void
    {
        const
            parentControl = this.parentControlGet();

        if (parentControl && ObjectHelper.isAssignableTo<IContainerControl>(parentControl, "@seriously-rad/seriouslyrad-framework-core/Ui/Controls/ContainerControl.ContainerControl"))
        {
            parentControl.prepend(this);
            return;
        }

        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        const
            $$parent = $$element.parent();

        $$parent.prepend($$element);
    }
    public show(): void
    {
        this.visible.set(true);
    }
    public viewElementGet(): JNative<HTMLElement>
    {
        return JNativeStatic.create$$({ nodeName: "p", text: this.text.get() ?? "" });
    }
    public updateVisibility(): void
    {
        this.isVisible.set((this.visible.get() ?? false) && (!this.visibleEvaluator.get() || (this.visibleEvaluator.get()?.value.get() ?? false)));
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.$$element = new Property<JNative<HTMLElement>>(this, "System.Object, System.Private.CoreLib", propertyValues.$$element, this._$$elementChanged);
        this.children = new Property<JsxFragment | string | (JsxFragment | string | undefined)[]>(this, "System.Object, System.Private.CoreLib", propertyValues.children);
        this.controlClass = new PropertyString(this, propertyValues.controlClass, this._controlClassChanged);
        this.controlNamespace = new PropertyString(this, propertyValues.controlNamespace, this._controlNamespaceChanged);
        this.cssClass = new PropertyString(this, propertyValues.cssClass, this._cssClassChanged);
        this.dataSource = new Property<IDataSource>(this, "System.Object, System.Private.CoreLib", propertyValues.dataSource, this._dataSourceChanged);
        this.dataSourceControl = new Property<IControl>(this, "System.Object, System.Private.CoreLib", propertyValues.dataSourceControl, this._dataSourceControlChanged);
        this.dataSourceControlSelector = new PropertyString(this, propertyValues.dataSourceControlSelector, this._dataSourceControlSelectorChanged);
        this.designMode = new PropertyBoolean(this, propertyValues.designMode, this._designModeChanged);
        this.dimmed = new PropertyBoolean(this, propertyValues.dimmed, this._dimmedChanged);
        this.draggable = new PropertyBoolean(this, propertyValues.draggable, this._draggableChanged);
        this.elementTitle = new PropertyString(this, propertyValues.elementTitle, this._elementTitleChanged);
        this.elementTypeOverride = new PropertyString(this, propertyValues.elementTypeOverride, this._elementTypeOverrideChanged);
        this.enabled = new PropertyBoolean(this, propertyValues.enabled, this._enabledChanged);
        this.height = new PropertyInt32(this, propertyValues.height, this._heightChanged);
        this.inline = new PropertyBoolean(this, propertyValues.inline, this._inlineChanged);
        this.isVisible = new PropertyBoolean(this, propertyValues.isVisible, this._isVisibleChanged);
        this.left = new PropertyInt32(this, propertyValues.left, this._leftChanged);
        this.maxHeight = new PropertyInt32(this, propertyValues.maxHeight, this._maxHeightChanged);
        this.maxWidth = new PropertyInt32(this, propertyValues.maxWidth, this._maxWidthChanged);
        this.minHeight = new PropertyInt32(this, propertyValues.minHeight, this._minHeightChanged);
        this.minWidth = new PropertyInt32(this, propertyValues.minWidth, this._minWidthChanged);
        this.page = new Property<IPage>(this, "System.Object, System.Private.CoreLib", propertyValues.page, this._pageChanged, { activated: this.onPageActivated, deactivated: this.onPageDeactivated, loaded: this.onPageLoaded });
        this.pageVariableName = new PropertyString(this, propertyValues.pageVariableName, this._pageVariableNameChanged);
        this.position = new Property<{ left?: Nullable<number>, top?: Nullable<number> }>(this, "System.Object, System.Private.CoreLib");
        this.propagateInputEvents = new PropertyBoolean(this, propertyValues.propagateInputEvents, this._propagateInputEventsChanged);
        this.ref = new Property<ControlRef | ElementRef>(this, "System.Object, System.Private.CoreLib", null)
        this.size = new Property<{ height?: Nullable<number>, width?: Nullable<number> }>(this, "System.Object, System.Private.CoreLib", null);
        this.suspendLayout = new PropertyBoolean(this, propertyValues.suspendLayout, this._suspendLayoutChanged);
        this.tabStop = new PropertyBoolean(this, propertyValues.tabStop, this._tabStopChanged);
        this.tag = new PropertyObject(this, propertyValues.tag, this._tagChanged);
        this.text = new PropertyString(this, propertyValues.text, this._textChanged);
        this.top = new PropertyInt32(this, propertyValues.top, this._topChanged);
        this.visible = new PropertyBoolean(this, propertyValues.visible, this._visibleChanged);
        this.visibleEvaluator = new Property<IEvaluator>(this, "System.Object, System.Private.CoreLib", propertyValues.visibleEvaluator, this._visibleEvaluatorChanged);
        this.width = new PropertyInt32(this, propertyValues.width, this._widthChanged);

        this.position.overrideGet(next =>
        {
            return {
                left: this.left.get(),
                top: this.top.get()
            }
        })
        this.position.overrideSet((value, next) =>
        {
            if (!value)
                return;

            this.left.set(value.left !== undefined ? value.left : null);
            this.top.set(value.top !== undefined ? value.top : null);
        });

        this.size.overrideGet(next =>
        {
            return {
                width: this.width.get(),
                height: this.height.get()
            }
        })
        this.size.overrideSet((value, next) =>
        {
            if (!value)
                return;

            this.width.set(value.width !== undefined ? value.width : null);
            this.height.set(value.height !== undefined ? value.height : null);
        });
    }

    // #endregion

    // #region Event Handlers

    protected onAncestorChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        self._setPage();
        self.dataSourceControl.set(self._getDataSourceControl());
    }
    protected onAncestorVisibilityChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected onClick(evt: IJNativeEvent)
    {
        /* Intentionally empty */
    }
    protected onDataSourceAfterSaveChanges(evt: IJNativeEvent, eventData: any)
    {
        /* Intentionally empty */
    }
    protected onDataSourceBeforeSaveChanges(evt: IJNativeEvent, eventData: any)
    {
        /* Intentionally empty */
    }
    protected onDataSourceControlDataSourceChanged(evt: IJNativeEvent, change: PropertyChange<DataSource>)
    {
        const
            self = evt.data.self as this;

        self.dataSource.set(change.currentValue);
    }
    protected onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this;

        self.refreshFromDataSource();
    }
    protected onDataSourceValidating(evt: IJNativeEvent, eventData: any)
    {
        /* Intentionally empty */
    }
    protected onDataSourceValidationErrorsChanged(evt: IJNativeEvent, change: any)
    {
        /* Intentionally empty */
    }
    protected onKeyDown(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.propagateInputEvents.get())
            evt.stopPropagation();
    }
    protected onKeyPress(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.propagateInputEvents.get())
            evt.stopPropagation();
    }
    protected onKeyUp(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.propagateInputEvents.get())
            evt.stopPropagation();
    }
    protected onMouseUp(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (self.designMode.get())
            return;

        if (!self.propagateInputEvents.get())
            evt.stopPropagation();
    }
    protected onPageActivated(evt: IJNativeEvent, eventData: { promises: RadPromiseLike<any>[] })
    {
        /* Intentionally empty */
    }
    protected onPageDataSourceChanged(evt: IJNativeEvent, change: PropertyChange<DataSource>)
    {
        /* Intentionally empty */
    }
    protected onPageDeactivated(evt: IJNativeEvent, eventData: any)
    {
        /* Intentionally empty */
    }
    protected onPageLoaded(evt: IJNativeEvent, eventData: any)
    {
        /* Intentionally empty */
    }
    protected onParentChanged(evt: IJNativeEvent, change: PropertyChange<JNative<HTMLElement>>)
    {
        /* Intentionally empty */
    }
    protected onVisibleEvaluatorValueChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        self.updateVisibility();
    }

    // #endregion

    // #endregion
}

// #endregion

// #region Control Overlay Loader

export type ControlOverlayLoaderPropertyValues =
    ControlPropertyValues
    & {
        showSpinner: boolean;
    };

export type ControlOverlayLoaderProperties = {
    [name in keyof ControlOverlayLoaderPropertyValues]: Property<ControlOverlayLoaderPropertyValues[name]>
};

export interface IControlOverlayLoader extends IControl, ControlOverlayLoaderProperties
{
}

export class ControlOverlayLoader extends Control implements IControlOverlayLoader
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Control";
    public static override className = "ControlOverlayLoader";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<ControlOverlayLoaderPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                showSpinner: true,
                text: "Loading",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected dateShown?: Date;
    protected hideTimeoutId?: number;

    // #endregion

    // #region Properties

    public showSpinner!: PropertyBoolean;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<ControlOverlayLoaderPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-control-overlay-loader ui segment");

        // Trigger Property Changes
        this.showSpinner.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public hideImmediately()
    {
        super.hide();
    }

    // #endregion

    // #region Protected Methods

    protected _showSpinnerChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.$$element.get()?.find(".ui.text").addClass("loader");
        else
            this.$$element.get()?.find(".ui.text").removeClass("loader");
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: ControlOverlayLoaderPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.showSpinner = new PropertyBoolean(this, propertyValues.showSpinner, this._showSpinnerChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        Assert.isNotNullOrUndefined($$element, "$$element");

        $$element.append(
            JNativeStatic.create([
                { nodeName: "div", attributes: { "class": "ui active dimmer inverted" } },
                { nodeName: "div", attributes: { "class": "ui text loader", text: "Loading" } },
                { nodeName: "p" }
            ])
        );

        return $$element;
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);
        this.$$element.get()?.find(".ui.text").text(change.currentValue);
    }

    public override hide()
    {
        if (this.hideTimeoutId)
            return;

        this.hideTimeoutId =
            window.setTimeout(
                () =>
                {
                    this.hideTimeoutId = 0;
                    this.hideImmediately();
                },
                Math.min((new Date()).getTime() - (this.dateShown?.getTime() ?? 0), 200)
            );
    }
    public override show()
    {
        if (this.hideTimeoutId)
        {
            clearTimeout(this.hideTimeoutId);
            this.hideTimeoutId = 0;
        }

        this.dateShown = new Date();
        super.show();
    }

    // #endregion

    // #endregion
}

// #endregion